import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Eye, Upload } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("viewer");
  const [newsletterOptin, setNewsletterOptin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await register(email, password, name, role, newsletterOptin);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome to Christoreel, ${res.user.name}!`);
      nav(role === "creator" ? "/creator" : "/subscription");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full overflow-hidden bg-[#FDF0CA]"><img src="/logo192.png" alt="Christoreel" className="h-9 w-9 object-cover" /></span>
            <span className="font-display text-2xl font-black">Christoreel</span>
          </Link>
          <h1 className="font-display text-4xl font-black">Create your account</h1>
          <p className="text-white/50 mt-2 text-sm">Join in seconds. Cancel anytime.</p>
          {/* eslint-disable-next-line react/no-unescaped-entities */}

          <div className="mt-6">
            <label className="text-eyebrow mb-2 block">I'm joining as a...</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRole("viewer")}
                data-testid="role-viewer-button"
                className={`p-4 rounded-lg border transition-all text-left ${role === "viewer" ? "border-[#E65F2B] bg-[#E65F2B]/10" : "border-white/10 hover:border-white/30"}`}
              >
                <Eye className="h-5 w-5 text-[#E65F2B] mb-2" />
                <p className="font-display font-bold">Viewer</p>
                <p className="text-xs text-white/50 mt-1">Watch and subscribe</p>
              </button>
              <button
                type="button"
                onClick={() => setRole("creator")}
                data-testid="role-creator-button"
                className={`p-4 rounded-lg border transition-all text-left ${role === "creator" ? "border-[#E65F2B] bg-[#E65F2B]/10" : "border-white/10 hover:border-white/30"}`}
              >
                <Upload className="h-5 w-5 text-[#EAB308] mb-2" />
                <p className="font-display font-bold">Creator</p>
                <p className="text-xs text-white/50 mt-1">Upload movies</p>
              </button>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-eyebrow mb-2 block">Full Name</label>
              <input required value={name} onChange={(e) => setName(e.target.value)} className="input-dark" data-testid="register-name-input" placeholder="Your full name" />
            </div>
            <div>
              <label className="text-eyebrow mb-2 block">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark" data-testid="register-email-input" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-eyebrow mb-2 block">Password (min 6)</label>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark" data-testid="register-password-input" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
            <label className="flex items-start gap-3 cursor-pointer py-2" data-testid="newsletter-optin-label">
              <input
                type="checkbox"
                checked={newsletterOptin}
                onChange={(e) => setNewsletterOptin(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-transparent accent-[#E65F2B]"
                data-testid="register-newsletter-optin"
              />
              <span className="text-sm text-white/60 leading-snug">
                Send me the Christoreel newsletter — new films, filmmaker stories. One-click unsubscribe.
              </span>
            </label>
            <button type="submit" disabled={loading} className="ember-btn w-full justify-center" data-testid="register-submit-button">
              {loading ? "Creating account..." : "Create account"}
            </button>
          </div>

          <p className="mt-6 text-sm text-white/50 text-center">
            Already a member? <Link to="/login" className="text-[#E65F2B] hover:text-[#F97316] font-semibold" data-testid="register-login-link">Sign in</Link>
          </p>
        </form>
      </div>
      <div className="hidden lg:flex relative overflow-hidden items-end p-12 order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1647202178320-a97da7a8251f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxjaW5lbWF0aWMlMjBhZnJpY2FuJTIwcG9ydHJhaXQlMjBkYXJrJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzgxOTgwODY4fDA&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-bl from-[#0A0A0A] via-transparent to-transparent" />
        <div className="relative">
          <p className="text-eyebrow">Christoreel</p>
          <h2 className="font-display text-5xl font-black mt-3 leading-tight">Where stories of <br /> faith find a stage.</h2>
        </div>
      </div>
    </div>
  );
}
